import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { SigningInformation } from './components/SigningInformation/SigningInformation';
import { ThirdPartyCompensation } from './components/ThirdPartyCompensation/ThirdPartyCompensation';
import { ApplicationEntryLayout } from '../components/ApplicationEntryLayout/ApplicationEntryLayout.tsx';
import { useValidateAndSaveApplication } from '../hooks/useValidateAndSaveApplication.ts';
import { ValidationGroup } from 'devextreme-react';
import { SignaturePopup } from '@apply/components/src/components/controls/SignaturePopup.tsx';
import parse from 'html-react-parser';
import { LoginApi } from '@apply/api/src/api/loginApi.ts';
import { applicationInsights } from '../../ApplicationInsights.js';
import { logApplicationInsightsError } from '../../common/functions/applicationInsightFunctions.ts';
import { Agreements } from './components/Agreements/Agreements.tsx';
import { useShowTermsheet } from './hooks/useShowTermsheet.ts';
import { useSaveApplicationWithoutValidation } from '../hooks/useSaveApplicationWithoutValidation.ts';
import { useRedirectIfComplete } from '../hooks/useRedirectIfApplicationComplete.ts';
import { CurrentApplicationPage, useNavigateApplication } from '../hooks/useNavigateApplication.ts';
import { usePausePointTracker } from '../hooks/usePausePointTracker.tsx';
import { useAuthentication } from '../../Login/hooks/useAuthentication';
import { useWaitOverlayStore } from '@apply/components';
import { useApplicationQuery } from '../../queries/applicationQueries';
import {
    useBorrowerEsignIpAddress,
    useBorrowerEsignAcknowledgeFont,
    useBorrowerEsignTermSheetFont,
    useBorrowerEsignTypedName,
    useBorrowerEsignTitle,
    useBorrowerEsignDate,
} from '../../queries/BorrowerFields/EsignFields';
import { useBorrowerThirdPartyExpectation, useBorrowerThirdPartyName } from '../../queries/BorrowerFields/ThirdPartyFields';
import { ApplicationCrumbType } from '../components/ApplicationEntryLayout/components/ApplicationEntrySections/types/ApplicationCrumbType.tsx';

export const Finalize: React.FC = () => {
    const { entryGuid } = useParams<{ entryGuid?: string }>();
    const { isPending } = useApplicationQuery(entryGuid);

    useEffect(() => {
        if (!isPending) {
            useWaitOverlayStore.getState().setWaiting(false);
        }
    }, [isPending]);

    const FONT_ID = 1;
    const loginApi = new LoginApi(applicationInsights);
    const navigate = useNavigate();
    const { authentication } = useAuthentication();
    const { navigateApplicationForward, navigateApplicationBackward } = useNavigateApplication({
        page: CurrentApplicationPage.Finalize,
        entryGuid,
    });
    const validationGroupRef = useRef(null);
    const { validateAndSaveApplication } = useValidateAndSaveApplication(validationGroupRef);
    const { saveApplicationNoValidation } = useSaveApplicationWithoutValidation();
    const { showTermsheet } = useShowTermsheet();
    const [, set_ipAddress] = useBorrowerEsignIpAddress();
    const [esignAcknowledgeFont, set_esignAcknowledgeFont] = useBorrowerEsignAcknowledgeFont();
    const [esignTermSheetFont, set_esignTermSheetFont] = useBorrowerEsignTermSheetFont();
    const [typedName] = useBorrowerEsignTypedName();
    const [title] = useBorrowerEsignTitle();
    const [date] = useBorrowerEsignDate();
    const [finalizeThirdPartyClaim] = useBorrowerThirdPartyExpectation();
    const [finalizeThirdPartyName] = useBorrowerThirdPartyName();
    const [acknowledgeHtml, set_acknowledgeHtml] = useState<string | null>(null);
    const [termSheetHtml, set_termSheetHtml] = useState<string | null>(null);
    const [buttonLock, set_buttonLock] = useState<boolean>(false);
    const { updatePausePoint, clearPausePoint } = usePausePointTracker();

    useRedirectIfComplete();

    useEffect(() => {
        const fetchIpAddress = async () => {
            const response = await loginApi.getIpAddress();

            if (response.statusCode !== 200) {
                logApplicationInsightsError(
                    new Error('Failed to retrieve IP address! External service failed to return IP address.')
                );
                return;
            }

            set_ipAddress(response.result);
        };

        fetchIpAddress();
    }, []);

    if (isPending && entryGuid) {
        return null;
    }

    const disableSubmit = () => {
        if (buttonLock) return true;

        if (!showTermsheet) {
            return !typedName || !title || !date || !esignAcknowledgeFont;
        } else {
            return (
                !typedName ||
                !title ||
                !date ||
                !esignAcknowledgeFont ||
                !esignTermSheetFont ||
                finalizeThirdPartyClaim === null ||
                (finalizeThirdPartyClaim && !finalizeThirdPartyName)
            );
        }
    };

    const saveApplication = async () => {
        clearPausePoint();

        set_buttonLock(true);

        try {
            const applicationResponse = await validateAndSaveApplication(true);
            if (!applicationResponse) return;

            navigateApplicationForward();
        } finally {
            set_buttonLock(false);
        }
    };

    const onConfirmAcknowledge = () => {
        set_acknowledgeHtml(null);
        set_esignAcknowledgeFont(FONT_ID);
    };

    const onRejectAcknowledge = () => {
        set_acknowledgeHtml(null);
        set_esignAcknowledgeFont(null);
    };

    const onConfirmTermsheet = () => {
        set_termSheetHtml(null);
        set_esignTermSheetFont(FONT_ID);
    };

    const onRejectTermsheet = () => {
        set_termSheetHtml(null);
        set_esignTermSheetFont(null);
    };

    const onSaveAndExit = async (e: React.MouseEvent<Element, MouseEvent>) => {
        if (e) e.preventDefault();

        updatePausePoint();

        const applicationResponse = await saveApplicationNoValidation();

        if (!applicationResponse) return;

        navigate('/home');
    };

    const nextStepButton = {
        id: 'signApplicationButton',
        dataTestid: 'signApplicationButton',
        disabled: disableSubmit(),
        children: 'Sign',
        handleClick: saveApplication,
    };

    const saveAndExitButton = {
        id: 'saveAndExitApplicationButton',
        dataTestid: 'saveAndExitApplicationButton',
        children: 'Save and Exit',
        handleClick: onSaveAndExit,
    };

    const previousStepButtonClick = async (e: React.MouseEvent<Element, MouseEvent>) => {
        if (e) e.preventDefault();

        clearPausePoint();

        const applicationResponse = await saveApplicationNoValidation();

        if (!applicationResponse) return;

        navigateApplicationBackward();
    };

    return (
        <ValidationGroup ref={validationGroupRef}>
            <ApplicationEntryLayout
                currentCrumb={ApplicationCrumbType.Review}
                saveAndExitButtonProps={saveAndExitButton}
                nextStepButtonProps={nextStepButton}
                previousStepButtonVisible={true}
                previousStepButtonClick={previousStepButtonClick}
            >
                <SigningInformation />
                {showTermsheet && <ThirdPartyCompensation />}
                <Agreements
                    set_acknowledgeHtml={set_acknowledgeHtml}
                    set_termSheetHtml={set_termSheetHtml}
                />
                <SignaturePopup
                    id={'acknowledgeSignaturePopeup'}
                    onSubmitAgreement={onConfirmAcknowledge}
                    onDeclineAgreement={onRejectAcknowledge}
                    isOpen={!!acknowledgeHtml}
                    firstName={authentication?.user.firstName ?? ''}
                    lastName={authentication?.user.lastName ?? ''}
                >
                    {parse(acknowledgeHtml ?? '')}
                </SignaturePopup>
                <SignaturePopup
                    id={'termsheetSignaturePopeup'}
                    onSubmitAgreement={onConfirmTermsheet}
                    onDeclineAgreement={onRejectTermsheet}
                    isOpen={!!termSheetHtml}
                    maxWidth={'80vw'}
                    firstName={authentication?.user.firstName ?? ''}
                    lastName={authentication?.user.lastName ?? ''}
                >
                    {parse(termSheetHtml ?? '')}
                </SignaturePopup>
            </ApplicationEntryLayout>
        </ValidationGroup>
    );
};
